import React from 'react';
import PropTypes from 'prop-types';
import { addDays } from 'date-fns';
import { DateRange } from 'react-date-range';
import theme from 'theme';
import { CalendarRangeType } from 'utils/PropTypesValues';
import { getPriorYear } from 'utils/dateUtils';
import Button from '../Button';

const CalendarRange = ({
  classes,
  calendarRange,
  onUpdateClicked,
  showClearButton,
  onClearClicked,
  clearLabel,
  buttonAlt,
  restrictToSingleDate,
  restrictToWeek,
  disableBtn,
}) => {
  const [range, setRange] = React.useState(calendarRange);
  const restrictionInUse = restrictToSingleDate || restrictToWeek;

  const minDate = getPriorYear(3);
  const maxDate = getPriorYear(-1);

  const isUpdateDisabled =
    range?.startDate < minDate.setHours(0, 0, 0, 0) || range?.endDate > maxDate.setHours(0, 0, 0, 0);

  const getAlteredDates = (newRange, restriction) => {
    switch (restriction) {
      case restrictToSingleDate:
        return { modifiedStart: newRange.startDate, modifiedEnd: newRange.endDate };
      case restrictToWeek:
        return { modifiedStart: addDays(newRange.startDate, 6), modifiedEnd: addDays(newRange.endDate, 6) };

      default:
        return { modifiedStart: newRange.startDate, modifiedEnd: newRange.endDate };
    }
  };

  const limitSelectionRange = (newRange) => {
    const { modifiedStart, modifiedEnd } = getAlteredDates(newRange, restrictionInUse);

    if (newRange.startDate !== range.startDate) {
      //Adjusts end date based on selected start date
      setRange({ startDate: newRange.startDate, endDate: modifiedStart, key: 'selection' });
    } else if (newRange.endDate !== range.endDate) {
      //Adjusts start date based on selected end date
      setRange({ startDate: newRange.endDate, endDate: modifiedEnd, key: 'selection' });
    }
  };

  const onChange = (newRange) => {
    if (restrictionInUse) return limitSelectionRange(newRange);
    setRange(newRange);
  };

  React.useEffect(() => {
    setRange(calendarRange);
  }, [calendarRange]);

  return (
    <>
      <div className={classes.calendar}>
        <DateRange
          rangeColors={[theme.color.black]}
          editableDateInputs
          onChange={(ranges) => onChange(ranges.selection)}
          className={classes.dateRange}
          ranges={[range]}
          dateDisplayFormat="MM/dd/yy"
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
      <div className={classes.footer}>
        {showClearButton && (
          <Button
            className={classes.calendarButton}
            label={clearLabel || 'clear'}
            variant="outlined"
            onClick={onClearClicked}
            data-testid="calendar-clear-btn"
          />
        )}
        <Button
          label={buttonAlt || 'update'}
          data-testid="calendar-update-btn"
          className={classes.calendarButton}
          onClick={() => onUpdateClicked(range)}
          disabled={disableBtn || isUpdateDisabled}
        />
      </div>
    </>
  );
};

CalendarRange.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  calendarRange: CalendarRangeType.isRequired,
  onUpdateClicked: PropTypes.func.isRequired,
  showClearButton: PropTypes.bool,
  onClearClicked: PropTypes.func,
  clearLabel: PropTypes.string,
  buttonAlt: PropTypes.string,
  restrictToSingleDate: PropTypes.bool,
  restrictToWeek: PropTypes.bool,
  disableBtn: PropTypes.bool,
};

CalendarRange.defaultProps = {
  classes: {},
  showClearButton: false,
  onClearClicked: () => {},
  clearLabel: '',
  buttonAlt: '',
  restrictToSingleDate: false,
  restrictToWeek: false,
  disableBtn: false,
};

export default CalendarRange;
